import { useEffect, useRef, useState, type CSSProperties } from 'react';
import {
  canStartPurchaseFlow,
  loadProUnlockProduct,
  purchaseProUnlock,
  restorePurchases,
  statusMessageFor,
  type ProProductInfo,
  type SubscriptionActionStatus,
} from '../lib/subscription.js';
import { LEGAL_LINKS } from '../lib/legal-links.js';

interface Props {
  feature: string;
  onClose: () => void;
  onUpgrade?: () => void;
}

/**
 * Whether the App Store has confirmed a sellable price for the Pro unlock.
 *
 * `loading`/`unavailable` MUST NOT show a price or an enabled Buy button — that combination
 * (a price the App Store has not confirmed it can sell, next to a live Buy button) is what
 * drew four straight Guideline 2.1(b) rejections. Only `ready` may show `displayPrice`.
 */
type ProductState = 'loading' | 'ready' | 'unavailable';

const PRO_FEATURES = [
  'Degree level, Square Align, back-to-back, 4-point saddle, kick, rolling offset, parallel offset, concentric, and segmented calculators',
  'Zeroable degree level for checking bend angles in the field',
  'One-tap level handoff from bend results',
  'Advanced bend layouts as Truebend grows',
];

export function PaywallSheet({ feature, onClose, onUpgrade }: Props) {
  const [actionStatus, setActionStatus] = useState<SubscriptionActionStatus | null>(null);
  const [workingAction, setWorkingAction] = useState<'purchase' | 'restore' | null>(null);
  // Which action produced the current status, so Try again retries THAT action rather
  // than always starting a purchase.
  const [statusAction, setStatusAction] = useState<'purchase' | 'restore' | null>(null);
  const purchasesAvailable = canStartPurchaseFlow();
  // Empty for 'cancelled' (and when nothing has happened yet), so the sheet stays silent
  // instead of reporting a non-problem. The unavailable panel below already explains the
  // no-App-Store case, so that does not get a status line either.
  const statusMessage = actionStatus ? statusMessageFor(actionStatus) : '';

  // The confirmed-price gate. Starts `loading` on every mount/retry so the price and Buy
  // button can never render before the App Store has actually confirmed a sellable product.
  const [productState, setProductState] = useState<ProductState>('loading');
  const [product, setProduct] = useState<ProProductInfo | null>(null);
  const [productAttempt, setProductAttempt] = useState(0);

  const sheetRef = useRef<HTMLElement>(null);

  useEffect(() => {
    sheetRef.current?.focus();
  }, []);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Fetch on open, and again whenever "Try again" bumps productAttempt. Nothing here ever
  // shows PRO_UNLOCK's hardcoded price — only whatever loadProUnlockProduct confirms.
  useEffect(() => {
    if (!purchasesAvailable) {
      setProductState('unavailable');
      return;
    }
    let cancelled = false;
    setProductState('loading');
    setProduct(null);
    // MUST catch, for the same reason the action handlers below do: an escaping rejection
    // hits the global 'unhandledrejection' listener and replaces the whole document with
    // the "Truebend needs a restart" screen — here that would fire as the paywall opens.
    void loadProUnlockProduct()
      .then((info) => {
        if (cancelled) return;
        if (info.available && info.displayPrice) {
          setProduct(info);
          setProductState('ready');
        } else {
          setProductState('unavailable');
        }
      })
      .catch(() => {
        if (cancelled) return;
        setProductState('unavailable');
      });
    return () => {
      cancelled = true;
    };
  }, [purchasesAvailable, productAttempt]);

  // NOTE: both handlers MUST catch. main.tsx installs a permanent global
  // 'unhandledrejection' listener (lib/startup-recovery.ts) that replaces the entire
  // document with a full-screen "Truebend needs a restart" alert. An escaping rejection
  // here — exactly when the reviewer taps Unlock Pro — would paint that over the app and
  // undo every other guarantee about not showing error text.
  async function handlePurchase() {
    // Defense in depth: the Buy button is already disabled unless productState === 'ready',
    // but never let a purchase start against an unconfirmed price even if it somehow fires.
    if (workingAction || productState !== 'ready') return;
    setActionStatus(null);
    setWorkingAction('purchase');
    try {
      const result = await purchaseProUnlock();
      setActionStatus(result.status);
      setStatusAction('purchase');
      if (result.status === 'purchased') {
        onUpgrade?.();
        if (!onUpgrade) onClose();
      }
    } catch {
      setActionStatus('unavailable');
      setStatusAction('purchase');
    } finally {
      setWorkingAction(null);
    }
  }

  async function handleRestore() {
    if (workingAction) return;
    setActionStatus(null);
    setWorkingAction('restore');
    try {
      const result = await restorePurchases();
      setActionStatus(result.status);
      setStatusAction('restore');
      if (result.status === 'restored') {
        onUpgrade?.();
        if (!onUpgrade) onClose();
      }
    } catch {
      setActionStatus('unavailable');
      setStatusAction('restore');
    } finally {
      setWorkingAction(null);
    }
  }

  return (
    <div onClick={onClose} role="presentation" style={overlayStyle}>
      <section
        ref={sheetRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="truebend-pro-title"
        onClick={(e) => e.stopPropagation()}
        style={sheetStyle}
      >
        <div style={handleStyle} aria-hidden="true" />
        <button type="button" onClick={onClose} aria-label="Close upgrade" style={closeButtonStyle}>
          <svg viewBox="0 0 24 24" aria-hidden="true" style={{ width: 18, height: 18 }}>
            <path
              d="m7 7 10 10M17 7 7 17"
              fill="none"
              stroke="currentColor"
              strokeLinecap="round"
              strokeWidth="2.2"
            />
          </svg>
        </button>

        <div style={previewStyle} aria-hidden="true">
          <svg viewBox="0 0 320 132" style={{ width: '100%', height: 'auto', display: 'block' }}>
            <rect x="10" y="12" width="300" height="108" rx="22" fill="var(--color-diagram-bg)" />
            <g opacity="0.25" stroke="var(--color-diagram-ghost)" strokeWidth="1">
              {Array.from({ length: 5 }).map((_, i) => (
                <line key={`h-${i}`} x1="28" y1={30 + i * 18} x2="292" y2={30 + i * 18} />
              ))}
              {Array.from({ length: 8 }).map((_, i) => (
                <line key={`v-${i}`} x1={44 + i * 32} y1="24" x2={44 + i * 32} y2="108" />
              ))}
            </g>
            <path
              d="M 36 88 L 92 88 L 138 44 L 186 44 L 228 88 L 284 88"
              fill="none"
              stroke="var(--color-diagram-pipe)"
              strokeWidth="10"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M 76 88 L 92 88 L 110 70"
              fill="none"
              stroke="var(--color-blue)"
              strokeWidth="10"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M 122 58 L 138 44 L 160 44"
              fill="none"
              stroke="var(--color-orange)"
              strokeWidth="10"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M 204 60 L 228 88 L 248 88"
              fill="none"
              stroke="var(--color-blue)"
              strokeWidth="10"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <text x="154" y="37" fill="var(--color-text-primary)" fontSize="12" fontWeight="900">
              Pro bends
            </text>
          </svg>
        </div>

        <div style={eyebrowStyle}>Truebend Pro</div>
        <h2 id="truebend-pro-title" style={titleStyle}>
          Unlock {feature}
        </h2>
        <p style={copyStyle}>
          Keep the free core simple. Unlock once when you need the larger bend library and faster
          jobsite setups.
        </p>

        <div style={featureListStyle}>
          {PRO_FEATURES.map((item) => (
            <div key={item} style={featureRowStyle}>
              <span aria-hidden="true" style={checkStyle}>
                <svg viewBox="0 0 16 16" style={{ width: 13, height: 13 }}>
                  <path
                    d="m3.2 8.2 3 3.1 6.6-6.9"
                    fill="none"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.2"
                  />
                </svg>
              </span>
              <span>{item}</span>
            </div>
          ))}
        </div>

        <div style={priceGridStyle}>
          {!purchasesAvailable ? (
            <div style={unavailablePanelStyle}>
              <div style={unavailableTitleStyle}>Pro requires the App Store</div>
              <div style={unavailableCopyStyle}>
                Truebend Pro is a one-time purchase available through the App Store on iPhone and
                iPad.
              </div>
            </div>
          ) : productState === 'ready' && product ? (
            <div style={priceSummaryStyle}>
              <div style={priceSummaryTopStyle}>
                <span>Truebend Pro</span>
                <span style={priceSummaryBadgeStyle}>One-time purchase</span>
              </div>
              <div className="mono" style={priceAmountStyle}>
                {product.displayPrice}
              </div>
              <div style={priceSummarySubStyle}>Buy once. Unlock forever.</div>
            </div>
          ) : productState === 'loading' ? (
            <div style={unavailablePanelStyle}>
              <div style={unavailableTitleStyle}>Checking pricing…</div>
              <div style={unavailableCopyStyle}>Confirming with the App Store.</div>
            </div>
          ) : (
            <div style={unavailablePanelStyle}>
              <div style={unavailableTitleStyle}>Pricing unavailable</div>
              <div style={unavailableCopyStyle}>
                Truebend couldn't confirm pricing with the App Store. Check your connection and try
                again.
              </div>
              <button
                type="button"
                onClick={() => setProductAttempt((n) => n + 1)}
                style={retryButtonStyle}
              >
                Try again
              </button>
            </div>
          )}
        </div>

        {purchasesAvailable ? (
          <>
            <button
              type="button"
              onClick={() => void handlePurchase()}
              disabled={workingAction !== null || productState !== 'ready'}
              style={
                workingAction !== null || productState !== 'ready'
                  ? primaryButtonDisabledStyle
                  : primaryButtonStyle
              }
            >
              {workingAction === 'purchase'
                ? 'Connecting…'
                : productState === 'ready' && product
                  ? `Unlock Pro — ${product.displayPrice}`
                  : productState === 'loading'
                    ? 'Checking pricing…'
                    : 'Not available right now'}
            </button>
            <button
              type="button"
              onClick={() => void handleRestore()}
              disabled={workingAction !== null}
              style={workingAction !== null ? restoreButtonDisabledStyle : restoreButtonStyle}
            >
              {workingAction === 'restore' ? 'Restoring' : 'Restore Purchases'}
            </button>
          </>
        ) : (
          <button type="button" disabled style={primaryButtonDisabledStyle}>
            Available on the App Store
          </button>
        )}
        {statusMessage && (
          <div role="status" aria-live="polite" style={statusStyle}>
            <span>{statusMessage}</span>
            {actionStatus === 'unavailable' && (
              <button
                type="button"
                onClick={() =>
                  void (statusAction === 'restore' ? handleRestore() : handlePurchase())
                }
                disabled={workingAction !== null}
                style={retryButtonStyle}
              >
                Try again
              </button>
            )}
          </div>
        )}
        <button type="button" onClick={onClose} style={secondaryButtonStyle}>
          Not now
        </button>
        <div style={footnoteGroupStyle}>
          {purchasesAvailable && (
            <p style={footnoteStyle}>
              This is a one-time purchase, not a subscription — nothing to cancel, nothing
              recurring.
            </p>
          )}
          <p style={footnoteStyle}>
            App Store purchases can be restored on any device signed in to your Apple ID.
          </p>
          <div style={legalLinksStyle}>
            <a href={LEGAL_LINKS.support} target="_blank" rel="noreferrer" style={legalLinkStyle}>
              Support
            </a>
            <a href={LEGAL_LINKS.privacy} target="_blank" rel="noreferrer" style={legalLinkStyle}>
              Privacy Policy
            </a>
            <a href={LEGAL_LINKS.terms} target="_blank" rel="noreferrer" style={legalLinkStyle}>
              Terms of Use
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

const overlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(2, 6, 23, 0.64)',
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'center',
  padding: 'var(--space-2)',
  zIndex: 50,
  backdropFilter: 'blur(10px)',
};

const sheetStyle: CSSProperties = {
  position: 'relative',
  width: '100%',
  maxWidth: 460,
  maxHeight: 'calc(100dvh - 16px)',
  overflowY: 'auto',
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: '24px 24px var(--radius-lg) var(--radius-lg)',
  padding: 'var(--space-4)',
  boxShadow: 'var(--shadow-elevated)',
};

const handleStyle: CSSProperties = {
  width: 44,
  height: 5,
  borderRadius: 'var(--radius-pill)',
  background: 'var(--color-border)',
  margin: '0 auto var(--space-3)',
};

const closeButtonStyle: CSSProperties = {
  position: 'absolute',
  top: 16,
  right: 16,
  width: 44,
  height: 44,
  minWidth: 44,
  minHeight: 44,
  borderRadius: 'var(--radius-md)',
  background: 'var(--color-bg)',
  border: '1px solid var(--color-border)',
  color: 'var(--color-text-secondary)',
  display: 'grid',
  placeItems: 'center',
};

const previewStyle: CSSProperties = {
  borderRadius: 'var(--radius-lg)',
  background: 'var(--color-bg)',
  border: '1px solid var(--color-border)',
  padding: 'var(--space-2)',
  marginBottom: 'var(--space-3)',
};

const eyebrowStyle: CSSProperties = {
  color: 'var(--color-blue)',
  fontSize: '0.72rem',
  fontWeight: 900,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  marginBottom: 'var(--space-1)',
};

const titleStyle: CSSProperties = {
  fontSize: 'var(--text-3xl)',
  lineHeight: 1.04,
  marginBottom: 'var(--space-2)',
};

const copyStyle: CSSProperties = {
  color: 'var(--color-text-secondary)',
  margin: '0 0 var(--space-3)',
  fontSize: 'var(--text-base)',
};

const featureListStyle: CSSProperties = {
  display: 'grid',
  gap: 'var(--space-2)',
  marginBottom: 'var(--space-3)',
};

const featureRowStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '24px 1fr',
  alignItems: 'start',
  gap: 'var(--space-2)',
  color: 'var(--color-text-primary)',
  fontSize: 'var(--text-sm)',
  lineHeight: 1.35,
};

const checkStyle: CSSProperties = {
  width: 22,
  height: 22,
  borderRadius: '50%',
  display: 'grid',
  placeItems: 'center',
  color: 'white',
  background: 'var(--color-blue)',
};

const priceGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr',
  marginBottom: 'var(--space-3)',
};

const unavailablePanelStyle: CSSProperties = {
  padding: 'var(--space-3)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid rgba(0, 122, 255, 0.28)',
  background: 'var(--color-blue-soft)',
  minHeight: 96,
  display: 'grid',
  alignContent: 'center',
  gap: 'var(--space-1)',
};

const unavailableTitleStyle: CSSProperties = {
  color: 'var(--color-text-primary)',
  fontSize: 'var(--text-base)',
  fontWeight: 900,
};

const unavailableCopyStyle: CSSProperties = {
  color: 'var(--color-text-secondary)',
  fontSize: 'var(--text-sm)',
  lineHeight: 1.35,
};

const priceSummaryStyle: CSSProperties = {
  padding: 'var(--space-3)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--color-blue)',
  background: 'var(--color-blue-soft)',
  minHeight: 96,
  display: 'grid',
  alignContent: 'center',
  textAlign: 'left',
};

const priceSummaryTopStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 'var(--space-2)',
  color: 'var(--color-text-muted)',
  fontSize: '0.68rem',
  fontWeight: 900,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
};

const priceSummaryBadgeStyle: CSSProperties = {
  color: 'white',
  background: 'var(--color-blue)',
  borderRadius: 'var(--radius-pill)',
  padding: '2px var(--space-2)',
  fontSize: '0.6rem',
  letterSpacing: 0,
  whiteSpace: 'nowrap',
};

const priceAmountStyle: CSSProperties = {
  color: 'var(--color-text-primary)',
  fontSize: 'clamp(1.45rem, 7vw, 1.95rem)',
  fontWeight: 900,
  lineHeight: 1,
  marginTop: 'var(--space-2)',
};

const priceSummarySubStyle: CSSProperties = {
  color: 'var(--color-text-secondary)',
  fontSize: '0.68rem',
  fontWeight: 800,
  marginTop: 'var(--space-1)',
};

const primaryButtonStyle: CSSProperties = {
  width: '100%',
  background: 'var(--color-blue)',
  color: 'white',
  borderRadius: 'var(--radius-md)',
  padding: 'var(--space-3) var(--space-5)',
  fontWeight: 900,
  fontSize: 'var(--text-base)',
  boxShadow: '0 16px 30px rgba(0, 122, 255, 0.24)',
  marginBottom: 'var(--space-2)',
};

const primaryButtonDisabledStyle: CSSProperties = {
  ...primaryButtonStyle,
  background: 'var(--color-bg)',
  color: 'var(--color-text-secondary)',
  border: '1px solid var(--color-border)',
  boxShadow: 'none',
  cursor: 'not-allowed',
};

const restoreButtonStyle: CSSProperties = {
  width: '100%',
  color: 'var(--color-blue)',
  background: 'var(--color-blue-soft)',
  border: '1px solid rgba(0, 122, 255, 0.28)',
  borderRadius: 'var(--radius-md)',
  padding: 'var(--space-3) var(--space-5)',
  fontWeight: 900,
  fontSize: 'var(--text-sm)',
  marginBottom: 'var(--space-2)',
};

const restoreButtonDisabledStyle: CSSProperties = {
  ...restoreButtonStyle,
  color: 'var(--color-text-muted)',
  background: 'var(--color-bg)',
  border: '1px solid var(--color-border)',
  cursor: 'not-allowed',
};

const statusStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 'var(--space-2)',
  color: 'var(--color-text-secondary)',
  fontSize: 'var(--text-xs)',
  fontWeight: 800,
  textAlign: 'center',
  marginBottom: 'var(--space-2)',
};

const retryButtonStyle: CSSProperties = {
  color: 'var(--color-blue)',
  background: 'var(--color-blue-soft)',
  border: '1px solid rgba(0, 122, 255, 0.28)',
  borderRadius: 'var(--radius-pill)',
  padding: '6px var(--space-3)',
  minHeight: 32,
  fontSize: 'var(--text-xs)',
  fontWeight: 900,
};

const secondaryButtonStyle: CSSProperties = {
  width: '100%',
  color: 'var(--color-text-secondary)',
  padding: 'var(--space-2)',
  fontSize: 'var(--text-sm)',
};

const footnoteGroupStyle: CSSProperties = {
  display: 'grid',
  gap: 'var(--space-1)',
  marginTop: 'var(--space-2)',
};

const footnoteStyle: CSSProperties = {
  color: 'var(--color-text-muted)',
  fontSize: '0.68rem',
  lineHeight: 1.35,
  textAlign: 'center',
  margin: 0,
};

const legalLinksStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 'var(--space-3)',
  fontSize: '0.68rem',
  fontWeight: 900,
};

const legalLinkStyle: CSSProperties = {
  color: 'var(--color-blue)',
  textDecoration: 'none',
};
